import React from 'react';
import {
    Card,
    CardContent,
    Avatar,
    Typography,
    Box,
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { PublicUserProfile } from '../../services/userService';
import { FollowButton } from './FollowButton';

interface UserCardProps {
    user: PublicUserProfile;
    showFollowButton?: boolean;
}

export const UserCard: React.FC<UserCardProps> = ({ user, showFollowButton = true }) => {
    const { theme } = useTheme();
    const navigate = useNavigate();

    const handleCardClick = () => {
        navigate(`/hostlyclub/profile/${user.uid}`);
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                backgroundColor: theme.colors.card,
                borderRadius: theme.radii.medium,
                border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows.medium,
                    borderColor: theme.colors.primary,
                },
            }}
        >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Avatar
                    src={user.profilePictureUrl}
                    sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: theme.colors.primary,
                    }}
                >
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 'bold',
                                color: theme.colors.text.primary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {user.name}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.colors.text.secondary,
                                opacity: 0.7,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            @{user.nickname || user.email?.split('@')[0] || ''}
                        </Typography>
                    </Box>

                    {user.bio && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.colors.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mt: 0.5,
                            }}
                        >
                            {user.bio}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                            <strong>{user.postsCount || 0}</strong> posts
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                            <strong>{user.followersCount || 0}</strong> seguidores
                        </Typography>
                    </Box>
                </Box>

                {showFollowButton && (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <FollowButton targetUserId={user.uid} variant="outlined" size="small" />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
